import React, { Component } from "react";
import { Table, Tag, Space, Alert, Empty } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { ModalItem, ButtonItem } from "../../../../Components/Items";

/**
 * Modal único para los cuatro puntos de entrada de la validación de timbrado:
 * un empleado desde la lista, un empleado desde Editar, todos los empleados, y
 * las líneas de una prenómina.
 *
 * Con un solo resultado se pinta el detalle completo; con varios, una tabla con
 * el detalle desplegable. Es un Table de antd y no TableItem a propósito:
 * TableItem no respeta `pagination` ni `scroll`, y aquí hacen falta los dos.
 *
 * Props:
 *   visible, onCancel, loading
 *   resultados        [{ ok, nombre, rfc, errores[], advertencias[], sat }]
 *   titulo
 *   onDescargarXml    (item) => void   opcional; agrega el botón de XML
 *   onDescargarTodos  () => void       opcional
 *   footerExtra       nodo extra en la barra de botones (ej. "Timbrar de todas formas")
 *   mensajeVacio
 */

const COLOR_ERROR = "#ff4d4f";
const COLOR_AVISO = "#faad14";
const COLOR_OK = "#52c41a";

// Los hallazgos del SAT se cuentan como error: el CFDI se va a rechazar.
const totalErrores = (r) => (r.errores?.length || 0) + (r.sat?.hallazgos?.length || 0);

// "No se pudo preguntar" no es lo mismo que "no hay nada que reportar". Sin esto
// una línea cuya consulta al validador se cayó salía en verde como lista para
// timbrar, que es exactamente lo contrario de lo que pasó.
const satNoConsultado = (r) => !!r.sat?.error;

const EtiquetaEstado = ({ resultado }) => {
  const errores = totalErrores(resultado);
  const avisos = resultado.advertencias?.length || 0;

  if (errores > 0) {
    return (
      <Tag color="error" icon={<CloseCircleOutlined />}>
        {errores === 1 ? "1 problema" : `${errores} problemas`}
      </Tag>
    );
  }
  if (satNoConsultado(resultado)) {
    return (
      <Tag icon={<QuestionCircleOutlined />}>SAT no consultado</Tag>
    );
  }
  if (avisos > 0) {
    return (
      <Tag color="warning" icon={<WarningOutlined />}>
        {avisos === 1 ? "1 advertencia" : `${avisos} advertencias`}
      </Tag>
    );
  }
  return (
    <Tag color="success" icon={<CheckCircleOutlined />}>
      Listo para timbrar
    </Tag>
  );
};

const ListaHallazgos = ({ titulo, color, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 600, color, marginBottom: 4 }}>{titulo}</div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {items.map((h, i) => (
          <li key={i} style={{ marginBottom: 2 }}>
            {h.mensaje}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Detalle = ({ resultado }) => {
  const errores = resultado.errores || [];
  const avisos = resultado.advertencias || [];
  const hallazgosSat = resultado.sat?.hallazgos || [];
  const errorSat = resultado.sat?.error;

  const sinNada =
    errores.length === 0 && avisos.length === 0 && hallazgosSat.length === 0 && !errorSat;

  return (
    <div>
      {sinNada && (
        <Alert
          type="success"
          showIcon
          message={
            resultado.sat
              ? "Los datos del empleado están completos y el SAT los reconoce."
              : "Los datos del empleado están completos."
          }
          description={
            resultado.sat
              ? undefined
              : 'Falta consultar el padrón del SAT: usa "Consultar el SAT" para comprobar que el RFC, el nombre y el código postal coincidan con lo registrado.'
          }
        />
      )}
      <ListaHallazgos
        titulo="Datos que hay que corregir antes de timbrar"
        color={COLOR_ERROR}
        items={errores}
      />
      <ListaHallazgos
        titulo="Rechazos del SAT"
        color={COLOR_ERROR}
        items={hallazgosSat}
      />
      {errorSat && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message={errorSat}
          description="La revisión de los datos capturados sí se hizo; sólo no se pudo consultar el padrón del SAT. Vuelve a intentarlo antes de dar por buena esta revisión."
        />
      )}
      <ListaHallazgos
        titulo="Advertencias (se puede timbrar, pero revísalo)"
        color={COLOR_AVISO}
        items={avisos}
      />
      {resultado.errorTimbradoDetalle && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 4 }}
          message="Motivo del último rechazo del PAC"
          description={resultado.errorTimbradoDetalle}
        />
      )}
    </div>
  );
};

class ModalValidacionTimbrado extends Component {
  render() {
    const {
      visible,
      onCancel,
      loading,
      resultados = [],
      titulo = "Validación de datos para timbrar",
      onDescargarXml,
      onDescargarTodos,
      footerExtra,
      mensajeVacio = "No hay empleados que validar.",
      aviso,
    } = this.props;

    const unico = resultados.length === 1 ? resultados[0] : null;
    const conProblemas = resultados.filter((r) => totalErrores(r) > 0).length;
    const sinSat = resultados.filter(
      (r) => totalErrores(r) === 0 && satNoConsultado(r)
    ).length;
    const conAvisos = resultados.filter(
      (r) =>
        totalErrores(r) === 0
        && !satNoConsultado(r)
        && (r.advertencias?.length || 0) > 0
    ).length;

    const columnas = [
      {
        title: "Empleado",
        dataIndex: "nombre",
        key: "nombre",
        render: (nombre, r) => (
          <div>
            <div>{nombre || "(sin nombre)"}</div>
            {(r.codigo !== null && r.codigo !== undefined) || r.serieNumero ? (
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                {r.codigo !== null && r.codigo !== undefined ? `No. ${r.codigo}` : r.serieNumero}
              </div>
            ) : null}
          </div>
        ),
      },
      { title: "RFC", dataIndex: "rfc", key: "rfc", width: 140 },
      {
        title: "Estado",
        key: "estado",
        width: 160,
        render: (_, r) => <EtiquetaEstado resultado={r} />,
      },
      {
        title: "Qué falta",
        key: "resumen",
        render: (_, r) => {
          const todos = [...(r.errores || []), ...(r.sat?.hallazgos || [])];
          if (todos.length === 0) {
            if (satNoConsultado(r)) {
              return <span style={{ color: COLOR_AVISO }}>{r.sat.error}</span>;
            }
            const avisos = r.advertencias || [];
            if (avisos.length === 0) return <span style={{ color: COLOR_OK }}>—</span>;
            return <span style={{ color: COLOR_AVISO }}>{avisos[0].mensaje}</span>;
          }
          return (
            <span>
              {todos[0].mensaje}
              {todos.length > 1 && (
                <span style={{ color: "#8c8c8c" }}> (+{todos.length - 1} más)</span>
              )}
            </span>
          );
        },
      },
    ];

    if (onDescargarXml) {
      columnas.push({
        title: "XML",
        key: "xml",
        width: 70,
        align: "center",
        render: (_, r) => (
          <ButtonItem
            type="default"
            size="small"
            azul
            icon={<DownloadOutlined />}
            onClick={() => onDescargarXml(r)}
          />
        ),
      });
    }

    return (
      <ModalItem
        title={titulo}
        visible={visible}
        onCancel={onCancel}
        spinning={loading}
        width={unico ? (window.innerWidth > 1000 ? "50%" : "95%") : undefined}
      >
        {aviso && !loading && (
          <Alert type="info" showIcon message={aviso} style={{ marginTop: 10 }} />
        )}

        {resultados.length === 0 && !loading && (
          <Empty description={mensajeVacio} style={{ margin: "20px 0" }} />
        )}

        {unico && (
          <div style={{ paddingTop: 10 }}>
            <div style={{ marginBottom: 12 }}>
              <Space>
                <strong>{unico.nombre || "(sin nombre)"}</strong>
                <span style={{ color: "#8c8c8c" }}>{unico.rfc}</span>
                <EtiquetaEstado resultado={unico} />
              </Space>
            </div>
            <Detalle resultado={unico} />
          </div>
        )}

        {resultados.length > 1 && (
          <div style={{ paddingTop: 10 }}>
            <div style={{ marginBottom: 10 }}>
              <Space wrap>
                <Tag color="error">{conProblemas} con problemas</Tag>
                {sinSat > 0 && (
                  <Tag icon={<QuestionCircleOutlined />}>
                    {sinSat} sin consultar al SAT
                  </Tag>
                )}
                <Tag color="warning">{conAvisos} con advertencias</Tag>
                <Tag color="success">
                  {resultados.length - conProblemas - conAvisos - sinSat} listos
                </Tag>
              </Space>
            </div>
            <div style={{ overflowX: "auto" }}>
              <Table
                size="small"
                rowKey={(r) => String(r.lineaId || r.empleadoId)}
                dataSource={resultados}
                columns={columnas}
                pagination={{ pageSize: 25, showSizeChanger: false }}
                scroll={{ x: 900, y: 420 }}
                expandable={{
                  expandedRowRender: (r) => <Detalle resultado={r} />,
                  rowExpandable: (r) =>
                    totalErrores(r) > 0
                    || (r.advertencias?.length || 0) > 0
                    || satNoConsultado(r)
                    || !!r.errorTimbradoDetalle,
                }}
              />
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            paddingTop: 12,
            paddingBottom: 4,
          }}
        >
          {/* Con un solo resultado no hay tabla donde poner la columna del XML,
              así que el botón vive en la barra de abajo. */}
          {unico && onDescargarXml && (
            <ButtonItem
              type="default"
              azul
              icon={<DownloadOutlined />}
              onClick={() => onDescargarXml(unico)}
            >
              Descargar XML de prueba
            </ButtonItem>
          )}
          {!unico && onDescargarTodos && resultados.length > 0 && (
            <ButtonItem
              type="default"
              azul
              icon={<DownloadOutlined />}
              onClick={onDescargarTodos}
            >
              Descargar XML prueba (ZIP)
            </ButtonItem>
          )}
          {footerExtra}
          <ButtonItem type="primary" onClick={onCancel}>
            Cerrar
          </ButtonItem>
        </div>
      </ModalItem>
    );
  }
}

export default ModalValidacionTimbrado;
